import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ArrowRight, Plug } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ConnectorLogo from '../components/Connections/ConnectorLogo';
import ConnectionStatus from '../components/Connections/ConnectionStatus';
import PendingImportsPanel from '../components/Connections/PendingImportsPanel';
import { Actions as CONN_ACTIONS } from '../store/actions/connectionActions';

const isLinkedStatus = (status) => status === 'connected' || status === 'needs_organisation' || status === 'error';

const ctaLabel = (status) => {
  if (status === 'connected') return 'Manage';
  if (status === 'needs_organisation') return 'Continue setup';
  if (status === 'error') return 'Fix connection';
  return 'Connect';
};

const ConnectionsHub = ({
  catalog,
  list,
  importSummary,
  loading,
  canManage,
  listConnections,
  getConnectionCatalog,
  getImportSummary,
}) => {
  const history = useHistory();

  useEffect(() => {
    if (!canManage) return;
    listConnections();
    getConnectionCatalog();
    getImportSummary();
  }, [canManage]);

  const instanceByConnector = useMemo(() => {
    const map = {};
    (list || []).forEach((c) => {
      map[c.connectorId] = c;
    });
    return map;
  }, [list]);

  const sortedCatalog = useMemo(() => {
    const rank = (connector) => {
      const id = connector.connectorId || connector.id;
      const status = instanceByConnector[id]?.status || 'disconnected';
      if (!connector.connectable) return 3;
      if (status === 'connected') return 0;
      if (status === 'needs_organisation' || status === 'error') return 1;
      return 2;
    };
    return [...(catalog || [])].sort((a, b) => rank(a) - rank(b));
  }, [catalog, instanceByConnector]);

  if (!canManage) {
    return (
      <div className='p-6'>
        <p className='text-muted-foreground'>You need organisation manage permission to view Connections.</p>
      </div>
    );
  }

  return (
    <div className='space-y-6 p-6'>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold text-foreground flex items-center gap-2'>
            <Plug size={24} strokeWidth={1.5} />
            Connections
          </h1>
          <p className='text-sm text-muted-foreground'>
            Connect accounting systems, map dimensions, and import spend into intelligent-import review.
          </p>
        </div>
      </div>

      <PendingImportsPanel importSummary={importSummary} sourceFilter='connector' />

      <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {sortedCatalog.map((connector) => {
          const id = connector.connectorId || connector.id;
          const instance = instanceByConnector[id];
          const status = instance?.status || 'disconnected';
          const linked = isLinkedStatus(status);
          const cardClass = linked
            ? 'bg-card text-card-foreground border-primary/40 shadow-sm'
            : 'bg-card text-card-foreground border-border';

          return (
            <Card key={id || connector.label} className={cardClass}>
              <CardHeader className='flex flex-row items-start gap-3 space-y-0'>
                <ConnectorLogo
                  logoUrl={connector.logoUrl}
                  logoAlt={connector.logoAlt}
                  logoDarkUrl={connector.logoDarkUrl}
                />
                <div className='flex-1 space-y-1'>
                  <CardTitle className='text-lg'>{connector.label}</CardTitle>
                  <p className='text-xs uppercase tracking-wide text-muted-foreground'>{connector.vertical}</p>
                  <ConnectionStatus status={status} lastError={instance?.lastError} />
                  {linked && instance?.externalTenantName && (
                    <p className='text-sm font-medium text-foreground'>{instance.externalTenantName}</p>
                  )}
                </div>
              </CardHeader>
              <CardContent className='flex flex-wrap items-center gap-2'>
                {connector.connectable ? (
                  <Button
                    variant={linked && status === 'connected' ? 'outline' : 'default'}
                    onClick={() => {
                      if (!id) return;
                      history.push(`/connections/${id}`);
                    }}>
                    {ctaLabel(status)}
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </Button>
                ) : (
                  <Badge className='bg-muted text-muted-foreground'>Coming soon</Badge>
                )}
                {instance?.hasMappingConfig && (
                  <Badge className='bg-info-bg text-info'>Mapping saved</Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
        {loading?.catalog && !(catalog || []).length && (
          <p className='text-sm text-muted-foreground'>Loading connectors…</p>
        )}
      </div>

      <div className='text-sm text-muted-foreground'>
        File uploads and the full import queue live under{' '}
        <Link className='text-primary underline' to='/usage'>
          Usage Data
        </Link>{' '}
        and{' '}
        <Link className='text-primary underline' to='/intelligent-import'>
          Smart Import
        </Link>
        .
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  catalog: state.connections?.catalog,
  list: state.connections?.list,
  importSummary: state.connections?.importSummary,
  loading: state.connections?.loading,
  canManage: !!state?.organisation?.permissions?.organisation?.manage || !!state.profile?.details?.cyfAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  listConnections: () => dispatch(CONN_ACTIONS.listConnections()),
  getConnectionCatalog: () => dispatch(CONN_ACTIONS.getConnectionCatalog()),
  getImportSummary: () => dispatch(CONN_ACTIONS.getImportSummary()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConnectionsHub);
